import { state, DOM } from './state.js';
import { router, registerRoute, syncMacroSections } from './router.js';
import { navigateTo } from './navigation.js';
import { CONFIG } from './config.js';
import {
    renderHomeNavigation,
    renderMenuSelection,
    renderMajorSectionsUI,
    renderMacroTabs,
    renderProductsUI,
} from './menu.js';
import { toggleSidebar, surfaceConnError } from './ui.js';
import { renderReservaStep, initReservas } from './reservas.js';
import { renderEventosForm } from './eventos.js';
import { validateEventoForm, buildWhatsappUrl } from './services/eventos.service.js';
import { renderAdmin } from './admin.js';
import { PANEL_ROUTES, panelForPath } from './shared/panel-routes.js';
import { MenuService } from './services/menu.service.js';
import { showToast } from './components/shared/Toast.js';
import { toggleTheme } from './theme.js';

// ---------------------------------------------------------------------------
// Route handlers
// ---------------------------------------------------------------------------

function handleHome() {
    DOM.viewWelcome?.classList.remove('hidden');
    DOM.whatsappFloat?.classList.remove('hidden');
    renderHomeNavigation();
    if (DOM.products) DOM.products.style.height = 'auto';
    renderMajorSectionsUI();
}

function handleMenuSelection() {
    DOM.viewMenuSelection?.classList.remove('hidden');
    DOM.sectionSubtitle.textContent = 'Nuestra Carta';
    renderMenuSelection();
    if (DOM.products) DOM.products.style.height = 'auto';
    renderMajorSectionsUI();
}

function handleMenuSection({ section }) {
    const sectionConfig = CONFIG.SECCIONES_CARTA.find(s => s.id === section);
    if (!sectionConfig) {
        // Unknown sub-section — fall back to menu selection
        navigateTo('/carta', true);
        return;
    }

    DOM.viewMenu?.classList.remove('hidden');
    DOM.macroTabs?.classList.remove('hidden');

    const isNewSection = state.activeMajorSectionId !== section;
    state.activeMajorSectionId = section;

    if (isNewSection || !state.loading || DOM.products.innerHTML === '') {
        syncMacroSections(section);
        renderProductsUI();
    }

    renderMacroTabs();

    syncMacroSections(section);
    renderMajorSectionsUI();
}

function handleEventos() {
    DOM.viewEventos?.classList.remove('hidden');
    DOM.sectionSubtitle.textContent = 'Eventos';
    renderEventosForm();
    renderMajorSectionsUI();
}

function handleReservas() {
    DOM.viewReservas?.classList.remove('hidden');
    DOM.sectionSubtitle.textContent = 'Mesa de Amigos';
    renderReservaStep(state.reserva.step || 1);
    renderMajorSectionsUI();
}

/**
 * D1 (`staff-admin-access-split`): un único handler para los DOS paneles —
 * `/staff` (staff) y `/admin` (admin) — distinguidos solo por el
 * argumento `panel` que cada `registerRoute` le pasa. El shell, el gate y el
 * `#view-admin` compartido son los mismos; `renderAdmin(panel)` decide qué
 * secciones y qué título renderizar (o si muestra el acceso denegado en el
 * lugar, spec "Counter Role Never Reaches Admin Sections").
 * @param {'staff'|'admin'} panel
 */
function handleAdmin(panel) {
    // El panel es full-bleed: ocultamos el wrapper público (header/footer mobile)
    // y mostramos la sección de admin, que tiene su propia rail lateral.
    DOM.publicShell?.classList.add('hidden');
    // El float de WhatsApp es público — no debe flotar sobre el panel de staff.
    DOM.whatsappFloat?.classList.add('hidden');
    DOM.viewAdmin?.classList.remove('hidden');
    renderAdmin(panel);
}

// ---------------------------------------------------------------------------
// Route registration
// ---------------------------------------------------------------------------

registerRoute('/', handleHome);
registerRoute('/carta', handleMenuSelection);
registerRoute(/^\/carta\/(?<section>[^/]+)$/, ({ section }) => handleMenuSection({ section }));

registerRoute('/eventos', handleEventos);
registerRoute('/reservas', handleReservas);

// Panel de staff/admin: solo se registran cuando la feature está habilitada.
// Si está deshabilitada, ambas rutas caen al 404 handler del router (redirect
// + Sentry). D1: dos paneles, mismo handler. Route rename (owner decision,
// SIN redirects): `/staff` resuelve `panel:'staff'` y `/admin` resuelve
// `panel:'admin'` — una ruta por entrada de `PANEL_ROUTES`
// (`js/shared/panel-routes.js`, la misma fuente que usa `renderAdmin` para su
// panel por defecto). `/admin/gestion` ya no se registra: cae al 404 estándar
// del router como cualquier ruta desconocida.
if (CONFIG.FEATURES.ADMIN_ENABLED) {
    for (const [path, panel] of Object.entries(PANEL_ROUTES)) {
        registerRoute(path, () => handleAdmin(panel));
    }
}

// `/kiosco` ya no se registra (staff-admin-access-split, Slice A): Fichaje es
// ahora la pestaña `fichaje` del panel de staff, alcanzada desde /staff con
// el mismo login que Reservas y Bloqueos (specs/time-clock-kiosk#"Kiosk
// Employee Grid"). La ruta cae al 404 estándar del router.

// ---------------------------------------------------------------------------
// App bootstrap
// ---------------------------------------------------------------------------

/**
 * Initializes the application.
 */
async function initApp() {
    // Set current year in footer
    document.querySelectorAll('.current-year').forEach(el => { el.textContent = new Date().getFullYear(); });

    // Wire up global event delegation
    setupEventListeners();

    // Ya NO seguimos el prefers-color-scheme del SO: el default es claro y el
    // usuario cambia con el toggle (preferencia persistida). Por eso no se
    // registra `initSystemListener` — así el SO en dark no pisa el default claro.

    // Render base navigation (Sidebar)
    renderMajorSectionsUI();

    // First route dispatch
    router();

    // Initialize reservation wizard listeners
    initReservas();

    // Load Fudo data via service
    try {
        await MenuService.init();
        if (state.error) {
            surfaceConnError(state.error);
        }
    } catch (error) {
        window.Sentry?.captureException(error);
        showToast("No pudimos cargar la app. Refrescá la página.", { level: 'error', duration: 0 });
    } finally {
        // Re-dispatch after data is loaded so active public views reflect
        // real data. The admin panels never read the menu, and re-dispatching
        // there re-rendered the whole panel on its default tab, silently
        // undoing a tab the admin had already clicked (and wiping a half-typed
        // login). The route is read NOW, so a visitor who moved from the admin
        // to a public route while the menu loaded still gets that re-render.
        if (!isAdminRoute()) router();
    }
}

/**
 * `true` for the staff/admin panel routes (`/staff`, `/admin`).
 * @returns {boolean}
 */
function isAdminRoute() {
    return panelForPath(window.location.pathname) !== null;
}

/**
 * Global event delegation — handles all data-action clicks and browser navigation events.
 */
function setupEventListeners() {
    document.body.addEventListener('click', (e) => {
        const target = e.target.closest('[data-action]');
        if (!target) return;

        const action = target.dataset.action;
        const path = target.dataset.path;

        switch (action) {
            case 'navigate':
                navigateTo(path);
                break;
            case 'navigate-and-close':
                navigateTo(path);
                toggleSidebar(false);
                break;
            case 'toggle-sidebar':
                toggleSidebar(target.dataset.open === 'true');
                break;
            case 'close-sidebar':
                toggleSidebar(false);
                break;
            case 'toggle-theme':
                toggleTheme();
                break;
            case 'evento-consultar': {
                const nombre = document.getElementById('inputEventoNombre')?.value ?? '';
                const apellido = document.getElementById('inputEventoApellido')?.value ?? '';
                const personasRaw = document.getElementById('inputEventoPersonas')?.value ?? '';

                const err = validateEventoForm({ nombre, apellido, personas: personasRaw });
                if (err) {
                    showToast(err, { level: 'warning' });
                    return;
                }

                const url = buildWhatsappUrl(
                    { nombre: nombre.trim(), apellido: apellido.trim(), personas: Number(personasRaw) },
                    CONFIG.WHATSAPP,
                );
                window.open(url, '_blank');
                break;
            }
        }
    });

    window.addEventListener('popstate', router);
    window.addEventListener('app-route', router);
}

// Start when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initApp);
} else {
    initApp();
}
